import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { TotpEnforcer } from './components/TotpEnforcer';
import { isAdmin, isGlobalAdmin } from './services/auth';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ThemeToggle } from './components/shared/ThemeToggle';
import { UserIdentifier } from './components/shared/UserIdentifier';
import './App.css';

const Login = lazy(() => import('./components/Login').then(m => ({ default: m.Login })));
const ExerciseList = lazy(() => import('./components/ExerciseList').then(m => ({ default: m.ExerciseList })));
const RoomView = lazy(() => import('./components/RoomView/RoomView').then(m => ({ default: m.RoomView })));
const FacilitatorView = lazy(() => import('./components/FacilitatorView/FacilitatorView').then(m => ({ default: m.FacilitatorView })));
const EscalationDetailView = lazy(() => import('./components/EscalationDetail/EscalationDetailView').then(m => ({ default: m.EscalationDetailView })));
const AdminPage = lazy(() => import('./components/admin/AdminPage').then(m => ({ default: m.AdminPage })));

/** Facilitator/Admin only — non-admins are redirected to Room View */
function ProtectedFacilitatorView() {
  const { user } = useAuth();
  const { exerciseId } = useParams<{ exerciseId: string }>();
  if (user && !isAdmin(user)) {
    return <Navigate to={`/exercise/${exerciseId}/room`} replace />;
  }
  return (
    <Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>}>
      <FacilitatorView />
    </Suspense>
  );
}

/** Admin only — non-admins are redirected to exercise list */
function ProtectedEscalationDetailView() {
  const { user } = useAuth();
  if (user && !isAdmin(user)) {
    return <Navigate to="/" replace />;
  }
  return (
    <Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>}>
      <EscalationDetailView />
    </Suspense>
  );
}

/** Global Admin only — non-admins are redirected to home */
function ProtectedAdminPage() {
  const { user } = useAuth();
  if (user && !isGlobalAdmin(user)) {
    return <Navigate to="/" replace />;
  }
  return (
    <Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>}>
      <AdminPage />
    </Suspense>
  );
}

const IDLE_TIMEOUT_MS = 10 * 60 * 60 * 1000; // 10 hours for all authenticated users

/** Runs inside BrowserRouter so useIdleTimeout can call useNavigate() */
function AppRoutes() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  // Never idle-timeout on /login — signOut() destroys in-progress Cognito challenges (temp password, MFA setup).
  useIdleTimeout(isLoginPage ? 0 : IDLE_TIMEOUT_MS);
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();
  const { tokens, theme } = useTheme();

  const isRoomOrFacilitatorView = /^\/exercise\/[^/]+\/(room|facilitator)$/.test(location.pathname);
  const showGlobalHeader = user && !isRoomOrFacilitatorView;
  const isDark = theme === 'dark';

  useEffect(() => {
    const handleForbidden = () => {
      signOut();
      navigate('/login', { replace: true });
    };
    window.addEventListener('auth:forbidden', handleForbidden);
    return () => window.removeEventListener('auth:forbidden', handleForbidden);
  }, [navigate, signOut]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {showGlobalHeader && (
        <header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            flexShrink: 0,
            background: tokens.colors.headerBackground,
            color: tokens.colors.headerText,
            padding: '8px 16px',
            borderRadius: tokens.effects.borderRadius,
            margin: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
            TableTop Exercise
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {user && isGlobalAdmin(user) && (
              <Link to="/admin" style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600, padding: '6px 12px' }}>
                Admin
              </Link>
            )}
            {user && <UserIdentifier user={user} />}
            <button
              type="button"
              onClick={handleSignOut}
              title="Logout"
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: 600,
                backgroundColor: isDark ? 'transparent' : '#78909C',
                color: isDark ? '#78909C' : 'white',
                border: isDark ? '1px solid #78909C' : 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Logout
            </button>
            <ThemeToggle />
          </div>
        </header>
      )}
      {user ? (
        <TotpEnforcer user={user}>
          <Routes>
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route
              path="/"
              element={<Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>}><ExerciseList /></Suspense>}
            />
            <Route
              path="/exercise/:exerciseId/room"
              element={<Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>}><RoomView /></Suspense>}
            />
            <Route
              path="/exercise/:exerciseId/facilitator"
              element={<ProtectedFacilitatorView />}
            />
            <Route
              path="/escalation/:escalationId"
              element={<ProtectedEscalationDetailView />}
            />
            <Route path="/admin" element={<ProtectedAdminPage />} />
          </Routes>
        </TotpEnforcer>
      ) : (
        <Routes>
          <Route path="/login" element={<Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>}><Login /></Suspense>} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
